import { useEffect, useState } from "react";
import type { AppliedDiscount } from "@/types/domain";
import {
  isFreeAccessCode,
  isLifetimeFree as isLifetimeFreeDiscount,
  isApplicableToBillingInterval,
  billingIntervalRejectionMessage,
  durationInMonths,
} from "@produce/shared";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getCurrentUserSession } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Check,
  Loader2,
  Infinity as InfinityIcon,
  Users,
  ArrowRight,
  LogOut,
  Smartphone,
} from "lucide-react";
import { toast } from "sonner";
import logo from "@/assets/logo-white.png";
import { VerificationRequired } from "@/components/VerificationRequired";
import { useNativelyEnvironment } from "@/hooks/useNativelyEnvironment";
import { safeLocalStorage } from "@/lib/safeStorage";

export default function UpgradePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { subscriptionStatus, refreshSubscription, refreshRoles } = useAuth();
  const queryClient = useQueryClient();
  const { isNativelyApp, isReady } = useNativelyEnvironment();
  // login_email is appended by the mobile crew app's "Upgrade to
  // Producer" banner — it identifies which user tapped the link so
  // we can (a) bounce a different web session out and (b) pre-fill
  // the login form on the redirect.
  const loginEmailHint = searchParams.get("login_email");
  const [billingInterval, setBillingInterval] = useState<"monthly" | "yearly">("monthly");
  const [selectedTier, setSelectedTier] = useState<"lite" | "pro" | "studio">("pro");
  const [discountCode, setDiscountCode] = useState(() => {
    const saved = safeLocalStorage.getItem("produce_early_access_discount");
    if (saved) {
      safeLocalStorage.removeItem("produce_early_access_discount");
      return saved;
    }
    return "";
  });
  const [referralCode, setReferralCode] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState<AppliedDiscount | null>(null);
  const [appliedReferral, setAppliedReferral] = useState<string | null>(null);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);

  const { data: session, isLoading: isSessionLoading } = useQuery({
    queryKey: ["user-session"],
    queryFn: getCurrentUserSession,
  });

  // Auth guard. Three cases:
  //   1. No session → redirect to producer-login with returnTo=/upgrade
  //      so the user lands back here after signing in. Pre-fill the
  //      email if the mobile banner sent a hint.
  //   2. Session exists but a different email than the mobile-banner
  //      hint → sign out the foreign session and bounce through login
  //      so the right user ends up here. Without this a producer X
  //      already logged in on the laptop would silently see crew
  //      member Y's upgrade page.
  //   3. Session matches (or no hint) → render normally.
  // Gated on isSessionLoading so we don't redirect during the initial
  // session fetch; the redirect would otherwise fire on every cold
  // load.
  useEffect(() => {
    if (isSessionLoading) return;
    const buildLoginQs = () => {
      const params = new URLSearchParams();
      params.set("returnTo", "/upgrade");
      if (loginEmailHint) params.set("email", loginEmailHint);
      return params.toString();
    };
    if (!session) {
      navigate(`/producer-login?${buildLoginQs()}`, { replace: true });
      return;
    }
    if (
      loginEmailHint &&
      session.email &&
      session.email.trim().toLowerCase() !== loginEmailHint.trim().toLowerCase()
    ) {
      (async () => {
        await supabase.auth.signOut();
        navigate(`/producer-login?${buildLoginQs()}`, { replace: true });
      })();
    }
  }, [isSessionLoading, session, loginEmailHint, navigate]);

  // Check email verification status
  const { data: profile } = useQuery({
    queryKey: ["user-profile", session?.userId],
    queryFn: async () => {
      if (!session?.userId) return null;

      const { data } = await supabase
        .from("profiles")
        .select("email_verified_at, email")
        .eq("id", session.userId)
        .single();

      return data;
    },
    enabled: !!session?.userId,
  });

  // Has this email ever used a producer trial? Look up by the
  // normalized email (lower(trim(email))) so a re-signup under the
  // same address can't reset the trial. The query is keyed on the
  // session's email rather than user_id because trial_history is
  // email-keyed (anti-abuse across accounts).
  const { data: hasUsedProducerTrial } = useQuery({
    queryKey: ["trial-history", session?.email],
    queryFn: async () => {
      if (!session?.email) return false;
      const normalized = session.email.trim().toLowerCase();
      const { data } = await supabase
        .from("trial_history")
        .select("trial_used")
        .eq("normalized_email", normalized)
        .eq("trial_used", true)
        .maybeSingle();
      return !!data;
    },
    enabled: !!session?.email,
  });

  // Base prices per tier
  const basePrices = {
    lite: { monthly: 29, yearly: 290 },
    pro: { monthly: 99, yearly: 990 },
    studio: { monthly: 199, yearly: 1990 },
  };

  // Calculate discounted prices
  const getDiscountedPrice = (tier: "lite" | "pro" | "studio", interval: "monthly" | "yearly") => {
    const basePrice = basePrices[tier][interval];
    if (!appliedDiscount) return basePrice;

    return appliedDiscount.discount_type === "percentage"
      ? basePrice * (1 - appliedDiscount.discount_value / 100)
      : basePrice - appliedDiscount.discount_value;
  };

  // Check if a plan is disabled by the discount's allowed_plans restriction
  const isPlanDisabledByDiscount = (tier: "lite" | "pro" | "studio") => {
    if (!appliedDiscount?.allowed_plans || appliedDiscount.allowed_plans.length === 0) return false;
    return !appliedDiscount.allowed_plans.includes(tier);
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;

    setIsApplyingDiscount(true);
    try {
      // Use case-insensitive search with ilike
      const { data, error } = await supabase
        .from("discount_codes")
        .select("*")
        .ilike("code", discountCode.trim())
        .eq("active", true)
        .single();

      if (error || !data) {
        toast.error("Invalid discount code");
        return;
      }

      // Check if expired
      if (data.expires_at && new Date(data.expires_at) < new Date()) {
        toast.error("This discount code has expired");
        return;
      }

      // Check usage limit
      if (data.usage_limit && (data.usage_count ?? 0) >= data.usage_limit) {
        toast.error("This discount code has reached its usage limit");
        return;
      }

      // A month-scoped (once/repeating) code on a yearly plan would discount
      // the first invoice — a whole year — so block it here too (the server
      // enforces this as well). Free-access codes bypass billing and are
      // exempt.
      if (!isApplicableToBillingInterval(data, billingInterval)) {
        toast.error(billingIntervalRejectionMessage(data));
        return;
      }

      setAppliedDiscount({
        code: data.code,
        discount_type: data.discount_type ?? "percentage",
        discount_value: data.discount_value,
        duration_type: data.duration_type ?? "once",
        duration_months: data.duration_months,
        allowed_plans: data.allowed_plans,
        grants_free_access: data.grants_free_access,
      });

      // If discount has allowed_plans restriction, auto-select that plan
      if (data.allowed_plans && data.allowed_plans.length === 1) {
        const forcedPlan = data.allowed_plans[0] as "lite" | "pro" | "studio";
        setSelectedTier(forcedPlan);
        toast.success(
          `Discount code applied! This code is only valid for the ${forcedPlan.toUpperCase()} plan.`,
        );
      } else if (data.allowed_plans && data.allowed_plans.length > 1) {
        // Multiple plans allowed, check if current selection is valid
        if (!data.allowed_plans.includes(selectedTier)) {
          setSelectedTier(data.allowed_plans[0] as "lite" | "pro" | "studio");
        }
        toast.success(
          `Discount code applied! Valid for: ${data.allowed_plans.map((p: string) => p.toUpperCase()).join(", ")}`,
        );
      } else {
        toast.success("Discount code applied!");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to apply discount code");
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  // Crew → producer 7-day trial. Calls the start_producer_trial RPC
  // (added in 20260507100000) which mirrors the producer branch of
  // handle_new_user: grants the producer role, creates a trial
  // producer_subscriptions row, and records trial_history. After
  // success, refresh the auth context (so producer access flips on)
  // and navigate to the dashboard.
  const handleStartTrial = async () => {
    if (!profile?.email_verified_at) {
      setShowVerificationDialog(true);
      return;
    }
    setIsProcessing(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)("start_producer_trial", {
        p_tier: selectedTier,
      });
      if (error) throw error;
      const status = (data as { status?: string } | null)?.status;
      if (status === "trial_already_used") {
        toast.error("You have already used your free trial. Please pick a paid plan.");
        // Re-query so the button flips back to Subscribe.
        await queryClient.invalidateQueries({ queryKey: ["trial-history", session?.email] });
        return;
      }
      toast.success(`Your ${selectedTier.toUpperCase()} trial has started!`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["user-session"] }),
        queryClient.invalidateQueries({ queryKey: ["user-profile", session?.userId] }),
        queryClient.invalidateQueries({ queryKey: ["trial-history", session?.email] }),
      ]);
      // BOTH refreshes must complete before navigate. ProducerRoute
      // gates /dashboard on hasProducerAccess = isProducer (from
      // roles) AND a valid subscription. Skipping refreshRoles meant
      // the navigate landed on /dashboard while roles still excluded
      // 'producer' → ProducerRoute bounced the user back to /upgrade.
      await Promise.all([refreshSubscription(), refreshRoles()]);
      navigate("/dashboard");
    } catch (error) {
      console.error(error);
      toast.error("Failed to start trial. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubscribe = async () => {
    // Check email verification status before allowing upgrade
    if (!profile?.email_verified_at) {
      setShowVerificationDialog(true);
      return;
    }

    setIsProcessing(true);

    try {
      // Free-access (comp) codes don't go through Stripe — no payment method,
      // no auto-charge. Grant the time-boxed subscription directly, refresh
      // auth so producer access flips on, and land on the dashboard.
      if (isFreeGrant) {
        const { data, error } = await supabase.functions.invoke("redeem-free-access", {
          body: { planTier: selectedTier, discountCode },
        });
        if (error) throw error;
        if (data?.error) {
          toast.error(data.error);
          return;
        }
        toast.success("Free access activated!");
        await Promise.all([refreshSubscription(), refreshRoles()]);
        navigate("/dashboard");
        return;
      }

      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          planTier: selectedTier,
          billingInterval: billingInterval,
          discountCode: discountCode || null,
          referralCode: appliedReferral || null,
        },
      });

      if (error) throw error;

      // Navigate the SAME tab to Stripe Checkout instead of opening
      // a new one. window.open(..., "_blank") after an await loses
      // the user-gesture context — Safari + Chrome popup blockers
      // then silently swallow the call and the producer sees
      // nothing happen. The Stripe session has success_url +
      // cancel_url pointing back at our origin, so the user lands
      // on /dashboard?subscription=success (or /upgrade?...=cancelled)
      // when they finish or back out.
      window.location.href = data.url;
    } catch (error) {
      console.error(error);
      toast.error("Failed to start subscription. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const trialDaysLeft = session?.trialEndsAt
    ? Math.max(
        0,
        Math.ceil((new Date(session.trialEndsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
      )
    : 0;

  // Check if user is crew-only (has crew role but no producer role)
  const isCrewOnlyUser = session?.roles?.includes("crew") && !session?.roles?.includes("producer");

  // Check if user already has a paid subscription (not trial)
  const hasActiveSubscription =
    subscriptionStatus?.subscribed &&
    subscriptionStatus?.plan_status === "active" &&
    subscriptionStatus?.plan_type !== "lifetime_free";

  // Check if it's a lifetime free discount (100% + forever) — shared logic.
  const isLifetimeFree = !!appliedDiscount && isLifetimeFreeDiscount(appliedDiscount);

  // A "free access" (comp) code: full access for a fixed period, no payment
  // method, no auto-charge — redeemed without Stripe checkout.
  const isFreeGrant = !!appliedDiscount && isFreeAccessCode(appliedDiscount);

  // Switching billing interval can invalidate an applied month-scoped code
  // (a "1 month" code can't go on a yearly plan). Drop it with a heads-up
  // rather than silently discounting a whole year.
  const handleSelectInterval = (interval: "monthly" | "yearly") => {
    setBillingInterval(interval);
    if (appliedDiscount && !isApplicableToBillingInterval(appliedDiscount, interval)) {
      setAppliedDiscount(null);
      setDiscountCode("");
      toast.info(billingIntervalRejectionMessage(appliedDiscount));
    }
  };

  // Get discount duration text
  const getDiscountDurationText = () => {
    if (!appliedDiscount) return null;

    if (isFreeGrant) {
      const months = durationInMonths(appliedDiscount);
      if (months === null) return "Free access (lifetime)";
      return `Free access for ${months} month${months > 1 ? "s" : ""}, then ends`;
    }

    if (appliedDiscount.duration_type === "forever") {
      return "Lifetime discount";
    } else if (appliedDiscount.duration_type === "repeating" && appliedDiscount.duration_months) {
      return `Discount valid for ${appliedDiscount.duration_months} month${appliedDiscount.duration_months > 1 ? "s" : ""}`;
    } else {
      return "Discount valid for first month";
    }
  };

  // Show native app message - upgrades only available via browser
  if (isReady && isNativelyApp) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10" />
        <Card className="relative z-10 max-w-md border-2 bg-card/50 p-8 text-center backdrop-blur-xl">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary to-secondary">
            <Smartphone className="h-8 w-8 text-white" />
          </div>
          <h2 className="mb-4 text-2xl font-bold">Upgrade via Browser</h2>
          <p className="mb-6 text-muted-foreground">
            To upgrade to a producer account, please visit{" "}
            <span className="font-medium text-primary">produce-app.com</span> in your web browser.
          </p>
          <p className="mb-6 text-sm text-muted-foreground">
            This app is designed for crew members to access their schedules and briefings. Producer
            account management is available on our web platform.
          </p>
          <Button variant="gradient" className="w-full" onClick={() => navigate("/crew-app")}>
            Back to Crew App
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-card/50 backdrop-blur-xl">
        <div className="container mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={logo} alt="PROduce Logo" className="h-10" />
          </Link>
          <div className="flex items-center gap-3">
            {isCrewOnlyUser ? (
              <Button variant="outline" onClick={() => navigate("/crew-app")}>
                Go to Crew Dashboard
              </Button>
            ) : (
              <Button variant="outline" onClick={() => navigate("/dashboard")}>
                Back to Dashboard
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={async () => {
                await supabase.auth.signOut();
                navigate("/producer-login");
              }}
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="relative overflow-hidden py-16 md:py-24">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-secondary/20" />
        <div className="container relative z-10 mx-auto px-6">
          <div className="mx-auto max-w-4xl">
            <div className="mb-8 text-center">
              <h1 className="mb-4 text-4xl font-bold md:text-5xl">
                {isCrewOnlyUser ? (
                  <>
                    Become a <span className="text-gradient-primary">Producer</span>
                  </>
                ) : hasActiveSubscription ? (
                  <>
                    Change your <span className="text-gradient-primary">Plan</span>
                  </>
                ) : (
                  <>
                    Upgrade to <span className="text-gradient-primary">PROduce</span>
                  </>
                )}
              </h1>
              {isCrewOnlyUser ? (
                <p className="text-xl text-muted-foreground">
                  Your account currently has crew functionalities only
                </p>
              ) : hasActiveSubscription ? (
                <p className="text-xl text-muted-foreground">
                  Currently on {subscriptionStatus?.plan_tier?.toUpperCase()} (
                  {subscriptionStatus?.plan_type === "yearly" ? "Yearly" : "Monthly"})
                </p>
              ) : trialDaysLeft > 0 ? (
                <p className="text-xl text-muted-foreground">
                  You have {trialDaysLeft} day{trialDaysLeft !== 1 ? "s" : ""} left in your trial
                  {!profile?.email_verified_at && (
                    <span className="mt-2 block text-sm text-yellow-500">
                      ⚠️ Remember to verify your email before upgrading
                    </span>
                  )}
                </p>
              ) : (
                <p className="text-xl text-muted-foreground">Your trial has ended</p>
              )}
            </div>

            {/* Crew-only user banner */}
            {isCrewOnlyUser && (
              <Card className="mb-8 border-2 border-blue-500/30 bg-gradient-to-br from-blue-500/10 to-purple-500/10 p-6">
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-blue-500/20 p-3">
                    <Users className="h-6 w-6 text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="mb-2 text-lg font-semibold">
                      You're signed in as a crew member
                    </h3>
                    <p className="mb-4 text-muted-foreground">
                      You're currently signed in as a crew member. To access producer features like
                      creating productions and managing crew, you'll need to upgrade to a producer
                      account.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        variant="outline"
                        onClick={() => navigate("/crew-app")}
                        className="gap-2"
                      >
                        Go to Crew Dashboard
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="default"
                        onClick={() => {
                          document
                            .getElementById("pricing-section")
                            ?.scrollIntoView({ behavior: "smooth" });
                        }}
                        className="gap-2"
                      >
                        Upgrade to Producer
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Billing Toggle */}
            <div id="pricing-section" className="mb-8 flex items-center justify-center gap-4">
              <button
                onClick={() => handleSelectInterval("monthly")}
                className={`rounded-full px-6 py-2 font-semibold transition-all ${
                  billingInterval === "monthly"
                    ? "bg-gradient-to-r from-primary to-secondary text-white"
                    : "bg-card text-muted-foreground hover:text-foreground"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => handleSelectInterval("yearly")}
                className={`rounded-full px-6 py-2 font-semibold transition-all ${
                  billingInterval === "yearly"
                    ? "bg-gradient-to-r from-primary to-secondary text-white"
                    : "bg-card text-muted-foreground hover:text-foreground"
                }`}
              >
                Yearly
                <Badge className="ml-2 bg-primary text-primary-foreground">Save 2 months</Badge>
              </button>
            </div>

            {isLifetimeFree ? (
              <Card className="mb-8 border-2 border-primary bg-gradient-to-br from-primary/20 to-secondary/20 p-8 backdrop-blur-xl">
                <div className="mb-6 flex items-center justify-between">
                  <h3 className="text-3xl font-bold">PROduce Lifetime Free</h3>
                  <Badge className="bg-gradient-to-r from-primary to-secondary text-white">
                    <InfinityIcon className="mr-1 h-4 w-4" />
                    Lifetime Free
                  </Badge>
                </div>

                <p className="mb-6 text-lg text-muted-foreground">
                  You have access to all features forever. No payment method required.
                </p>

                <div className="mb-6 grid gap-6 md:grid-cols-2">
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-primary" />
                      <span>Unlimited productions</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-primary" />
                      <span>Unlimited crew members</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-primary" />
                      <span>AI-powered scheduling</span>
                    </li>
                  </ul>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-primary" />
                      <span>Real-time updates</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-primary" />
                      <span>Priority support</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-primary" />
                      <span>Early access to features</span>
                    </li>
                  </ul>
                </div>

                <div className="rounded-lg bg-background/50 p-4 text-center">
                  <p className="text-sm text-muted-foreground">
                    💡 No credit card required • Cancel anytime • Forever free access
                  </p>
                </div>
              </Card>
            ) : (
              <div className="mb-8 grid gap-6 md:grid-cols-3">
                {/* LITE */}
                <Card
                  className={`border-2 bg-card/50 p-8 backdrop-blur-xl transition-all ${
                    isPlanDisabledByDiscount("lite")
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer hover:scale-105"
                  } ${
                    selectedTier === "lite"
                      ? "border-primary"
                      : isPlanDisabledByDiscount("lite")
                        ? ""
                        : "hover:border-primary"
                  }`}
                  onClick={() => !isPlanDisabledByDiscount("lite") && setSelectedTier("lite")}
                >
                  <h3 className="mb-2 text-2xl font-bold">LITE</h3>
                  <div className="mb-6">
                    <span className="text-5xl font-bold">
                      €{getDiscountedPrice("lite", billingInterval).toFixed(2)}
                    </span>
                    <span className="text-muted-foreground">
                      /{billingInterval === "monthly" ? "month" : "year"}
                    </span>
                    {billingInterval === "yearly" && (
                      <p className="mt-1 text-sm text-muted-foreground">
                        €{(getDiscountedPrice("lite", "yearly") / 12).toFixed(2)}/month
                      </p>
                    )}
                    {appliedDiscount && (
                      <div className="mt-2">
                        <span className="text-sm text-muted-foreground line-through">
                          €{basePrices.lite[billingInterval].toFixed(2)}
                        </span>
                        <Badge variant="secondary" className="ml-2">
                          {appliedDiscount.discount_type === "percentage"
                            ? `${appliedDiscount.discount_value}% off`
                            : `€${appliedDiscount.discount_value} off`}
                        </Badge>
                      </div>
                    )}
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-primary" />
                      <span>
                        <strong>1 crew member</strong> per production
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-primary" />
                      <span>Unlimited productions</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-primary" />
                      <span>AI-powered scheduling</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-primary" />
                      <span>Real-time app updates</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-primary" />
                      <span>Standard support</span>
                    </li>
                  </ul>
                </Card>

                {/* PRO */}
                <Card
                  className={`relative border-2 bg-card/50 p-8 backdrop-blur-xl transition-all ${
                    isPlanDisabledByDiscount("pro")
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer hover:scale-105"
                  } ${
                    selectedTier === "pro"
                      ? "border-primary"
                      : isPlanDisabledByDiscount("pro")
                        ? ""
                        : "hover:border-primary"
                  }`}
                  onClick={() => !isPlanDisabledByDiscount("pro") && setSelectedTier("pro")}
                >
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-primary to-secondary">
                    Most Popular
                  </Badge>
                  <h3 className="mb-2 mt-2 text-2xl font-bold">PRO</h3>
                  <div className="mb-6">
                    <span className="text-5xl font-bold">
                      €{getDiscountedPrice("pro", billingInterval).toFixed(2)}
                    </span>
                    <span className="text-muted-foreground">
                      /{billingInterval === "monthly" ? "month" : "year"}
                    </span>
                    {billingInterval === "yearly" && (
                      <p className="mt-1 text-sm text-muted-foreground">
                        €{(getDiscountedPrice("pro", "yearly") / 12).toFixed(2)}/month
                      </p>
                    )}
                    {appliedDiscount && (
                      <div className="mt-2">
                        <span className="text-sm text-muted-foreground line-through">
                          €{basePrices.pro[billingInterval].toFixed(2)}
                        </span>
                        <Badge variant="secondary" className="ml-2">
                          {appliedDiscount.discount_type === "percentage"
                            ? `${appliedDiscount.discount_value}% off`
                            : `€${appliedDiscount.discount_value} off`}
                        </Badge>
                      </div>
                    )}
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-primary" />
                      <span>
                        <strong>10 crew members</strong> per production
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-primary" />
                      <span>
                        <strong>10 productions</strong> per year
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-primary" />
                      <span>AI-powered scheduling</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-primary" />
                      <span>Real-time app updates</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-primary" />
                      <span>Live crew tracking</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-primary" />
                      <span>Priority support</span>
                    </li>
                  </ul>
                </Card>

                {/* STUDIO */}
                <Card
                  className={`border-2 bg-card/50 p-8 backdrop-blur-xl transition-all ${
                    isPlanDisabledByDiscount("studio")
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer hover:scale-105"
                  } ${
                    selectedTier === "studio"
                      ? "border-secondary"
                      : isPlanDisabledByDiscount("studio")
                        ? ""
                        : "hover:border-secondary"
                  }`}
                  onClick={() => !isPlanDisabledByDiscount("studio") && setSelectedTier("studio")}
                >
                  <h3 className="mb-2 text-2xl font-bold">STUDIO</h3>
                  <div className="mb-6">
                    <span className="text-5xl font-bold">
                      €{getDiscountedPrice("studio", billingInterval).toFixed(2)}
                    </span>
                    <span className="text-muted-foreground">
                      /{billingInterval === "monthly" ? "month" : "year"}
                    </span>
                    {billingInterval === "yearly" && (
                      <p className="mt-1 text-sm text-muted-foreground">
                        €{(getDiscountedPrice("studio", "yearly") / 12).toFixed(2)}/month
                      </p>
                    )}
                    {appliedDiscount && (
                      <div className="mt-2">
                        <span className="text-sm text-muted-foreground line-through">
                          €{basePrices.studio[billingInterval].toFixed(2)}
                        </span>
                        <Badge variant="secondary" className="ml-2">
                          {appliedDiscount.discount_type === "percentage"
                            ? `${appliedDiscount.discount_value}% off`
                            : `€${appliedDiscount.discount_value} off`}
                        </Badge>
                      </div>
                    )}
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-secondary" />
                      <span>
                        <strong>Unlimited crew members</strong>
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-secondary" />
                      <span>
                        <strong>Unlimited productions</strong>
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-secondary" />
                      <span>Everything in PRO</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-secondary" />
                      <span>Advanced analytics</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-secondary" />
                      <span>Dedicated support</span>
                    </li>
                  </ul>
                </Card>
              </div>
            )}

            <Card className="mb-8 border-2 bg-card/50 p-6 backdrop-blur-xl">
              <div className="mb-4 flex gap-3">
                <Input
                  placeholder="Discount code"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleApplyDiscount();
                    }
                  }}
                  className="flex-1"
                  disabled={!!appliedReferral}
                />
                <Button
                  variant="outline"
                  onClick={handleApplyDiscount}
                  disabled={isApplyingDiscount || !discountCode.trim() || !!appliedReferral}
                >
                  {isApplyingDiscount ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Applying...
                    </>
                  ) : (
                    "Apply"
                  )}
                </Button>
              </div>

              {/* Referral code input */}
              <div className="flex gap-3">
                <Input
                  placeholder="Referral code (e.g. REF-NAME-A3F2)"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  onKeyDown={async (e) => {
                    if (e.key === "Enter" && referralCode.trim()) {
                      // Validate referral code
                      const { data } = await supabase
                        .from("referral_codes")
                        .select("code")
                        .eq("code", referralCode.trim())
                        .maybeSingle();
                      if (data) {
                        setAppliedReferral(data.code);
                        setAppliedDiscount(null);
                        setDiscountCode("");
                        toast.success("Referral code applied! 20% off for 3 months.");
                      } else {
                        toast.error("Invalid referral code");
                      }
                    }
                  }}
                  className="flex-1"
                  disabled={!!appliedDiscount}
                />
                <Button
                  variant="outline"
                  onClick={async () => {
                    if (!referralCode.trim()) return;
                    const { data } = await supabase
                      .from("referral_codes")
                      .select("code")
                      .eq("code", referralCode.trim())
                      .maybeSingle();
                    if (data) {
                      setAppliedReferral(data.code);
                      setAppliedDiscount(null);
                      setDiscountCode("");
                      toast.success("Referral code applied! 20% off for 3 months.");
                    } else {
                      toast.error("Invalid referral code");
                    }
                  }}
                  disabled={!referralCode.trim() || !!appliedDiscount}
                >
                  Apply
                </Button>
              </div>

              {appliedReferral && (
                <div className="mt-4 rounded-lg border border-primary/20 bg-primary/5 p-4">
                  <div className="mb-1 flex items-center gap-2">
                    <Check className="h-5 w-5 text-primary" />
                    <span className="font-semibold text-primary">
                      Referral Applied: {appliedReferral}
                    </span>
                  </div>
                  <p className="text-sm text-foreground">20% off for 3 months</p>
                </div>
              )}

              {appliedDiscount && (
                <div className="mt-4 rounded-lg border border-primary/20 bg-primary/5 p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <Check className="h-5 w-5 text-primary" />
                    <span className="font-semibold text-primary">
                      Discount Applied: {appliedDiscount.code}
                    </span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p className="text-foreground">
                      {appliedDiscount.discount_type === "percentage"
                        ? `${appliedDiscount.discount_value}% off`
                        : `€${appliedDiscount.discount_value} off`}
                    </p>
                    <p className="font-medium text-primary">{getDiscountDurationText()}</p>
                    {appliedDiscount.duration_type === "repeating" &&
                      appliedDiscount.duration_months && (
                        <p className="text-xs text-muted-foreground">
                          Discount ends after {appliedDiscount.duration_months} billing cycle
                          {appliedDiscount.duration_months > 1 ? "s" : ""}
                        </p>
                      )}
                  </div>
                </div>
              )}
            </Card>

            {/* Trial-eligible crew users get a free-trial CTA instead
                of paid subscribe. Otherwise (already used trial,
                already producer, etc.) we fall through to Stripe
                checkout via handleSubscribe. */}
            {isCrewOnlyUser && !hasUsedProducerTrial && !isLifetimeFree && !isFreeGrant ? (
              <>
                <Button
                  variant="gradient"
                  size="lg"
                  className="mb-2 w-full"
                  onClick={handleStartTrial}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Starting trial...
                    </>
                  ) : (
                    `Start 7-day free trial (${selectedTier.toUpperCase()})`
                  )}
                </Button>
                <p className="mb-4 text-center text-sm text-muted-foreground">
                  No payment details required. Cancel anytime during the trial.
                </p>
              </>
            ) : (
              <>
                <Button
                  variant="gradient"
                  size="lg"
                  className="mb-4 w-full"
                  onClick={handleSubscribe}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : isLifetimeFree ? (
                    "Activate Lifetime Free Access"
                  ) : isFreeGrant ? (
                    getDiscountDurationText() || "Activate Free Access"
                  ) : hasActiveSubscription ? (
                    `Switch to ${selectedTier.toUpperCase()} (${billingInterval === "monthly" ? "Monthly" : "Yearly"}) - €${getDiscountedPrice(selectedTier, billingInterval).toFixed(2)}/${billingInterval === "monthly" ? "month" : "year"}`
                  ) : (
                    `Subscribe ${selectedTier.toUpperCase()} - €${getDiscountedPrice(selectedTier, billingInterval).toFixed(2)}/${billingInterval === "monthly" ? "month" : "year"}`
                  )}
                </Button>

                <p className="text-center text-sm text-muted-foreground">
                  Cancel anytime. No questions asked.
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Verification Required Dialog */}
      <VerificationRequired
        open={showVerificationDialog}
        onOpenChange={setShowVerificationDialog}
        userEmail={profile?.email || session?.email || ""}
      />
    </div>
  );
}
